import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
import { msg } from "@lit/localize";
import "@progressive-development/pd-forms/pd-button";
import { classMap } from "lit/directives/class-map.js";
import "@progressive-development/pd-forms/pd-input";
import "@progressive-development/pd-forms/pd-input-area";
//#region src/pd-badge-order/PdBadgeItem.ts
/**
* Single badge item used inside PdBadgeOrder.
* Pure visual component — no own logic beyond rendering and dispatching action events.
*
* @tagname pd-badge-item
* @fires badge-action - Fired when the action button (+/← /delete) is clicked. Detail: { action, badgeId }
*/
var PdBadgeItem = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.index = 0;
		this.numbered = false;
		this.active = false;
		this.editable = false;
		this.disabled = false;
		this.readonly = false;
		this.dragging = false;
		this.ghost = false;
		this.dimmed = false;
		this._editing = false;
		this._stopInternalFormEvent = (e) => {
			if (e.target !== this) e.stopPropagation();
		};
		this._onEditKeydown = (e) => {
			if (e.key === "Escape") {
				e.preventDefault();
				e.stopPropagation();
				this._onEditCancel(e);
				return;
			}
			if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
				e.preventDefault();
				e.stopPropagation();
				this._onEditSave(e);
			}
		};
	}
	connectedCallback() {
		super.connectedCallback();
		this.addEventListener("pd-form-element-register", this._stopInternalFormEvent);
		this.addEventListener("pd-form-element-change", this._stopInternalFormEvent);
	}
	disconnectedCallback() {
		this.removeEventListener("pd-form-element-register", this._stopInternalFormEvent);
		this.removeEventListener("pd-form-element-change", this._stopInternalFormEvent);
		this._teardownEditKeydown();
		super.disconnectedCallback();
	}
	updated(changed) {
		super.updated(changed);
		if (changed.has("_editing")) this.toggleAttribute("editing", this._editing);
	}
	_setupEditKeydown() {
		this.addEventListener("keydown", this._onEditKeydown);
	}
	_teardownEditKeydown() {
		this.removeEventListener("keydown", this._onEditKeydown);
	}
	static {
		this.styles = css`
    :host {
      display: block;
    }

    .badge {
      position: relative;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border-radius: var(--pd-badge-radius, 8px);
      background: var(--pd-default-bg-col, #fff);
      border: 1px solid var(--pd-default-disabled-light-col, #e5e7eb);
      transition:
        box-shadow 0.15s ease-out,
        transform 0.15s ease-out,
        opacity 0.15s ease-out;
      cursor: grab;
      user-select: none;
      min-height: var(--pd-badge-height, auto);
      will-change: transform;
      overflow: hidden;
    }

    .badge.dragging {
      cursor: grabbing;
    }

    .badge.disabled,
    .badge.readonly,
    .badge.ghost,
    .badge.editing {
      cursor: default;
    }

    .badge.has-color {
      padding-left: 0.85rem;
    }

    .badge.has-color::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--_badge-color, transparent);
      border-radius: var(--pd-badge-radius, 8px) 0 0 var(--pd-badge-radius, 8px);
    }

    .badge:hover:not(.disabled):not(.readonly):not(.ghost):not(.dragging) {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }

    :host([dimmed]) .badge {
      opacity: 0.6;
      pointer-events: none;
      transition: opacity 0.15s ease-out;
    }

    :host([dimmed]) .badge:hover {
      box-shadow: none;
      transform: none;
    }

    .badge.dragging {
      transform: scale(var(--pd-badge-drag-scale, 1.03));
      box-shadow: var(--pd-badge-drag-shadow, 0 8px 24px rgba(0, 0, 0, 0.15));
      z-index: 100;
    }

    .badge.ghost {
      opacity: 0.35;
      border-style: dashed;
      background: var(
        --pd-badge-ghost-color,
        var(--pd-default-light-col, #f3f4f6)
      );
    }

    .badge.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .badge.readonly {
      cursor: default;
    }

    .number {
      flex-shrink: 0;
      width: 1.5rem;
      text-align: center;
      font-weight: 600;
      font-size: 0.8rem;
      color: var(--pd-default-font-title-col, #374151);
      font-family: var(--pd-default-font-title-family, sans-serif);
    }

    .drag-handle {
      flex-shrink: 0;
      cursor: grab;
      display: flex;
      align-items: center;
      touch-action: none;
      --pd-icon-size: 1.1rem;
      color: var(--pd-default-disabled-col, #9ca3af);
      opacity: 0.5;
      transition:
        color 0.15s ease-out,
        opacity 0.15s ease-out;
    }

    .badge:hover .drag-handle {
      opacity: 1;
      color: var(--pd-default-font-col, #374151);
    }

    .badge.disabled .drag-handle,
    .badge.readonly .drag-handle {
      cursor: default;
      visibility: hidden;
    }

    .badge-icon {
      flex-shrink: 0;
      --pd-icon-size: 1.1rem;
    }

    .content {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
    }

    .title {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--pd-default-font-title-col, #374151);
      font-family: var(--pd-default-font-title-family, sans-serif);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .description {
      font-size: 0.75rem;
      color: var(--pd-default-font-col, #6b7280);
      font-family: var(--pd-default-font-input-family, sans-serif);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .action-zone {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 0.4rem;
      cursor: default;
      margin: -0.5rem -0.75rem -0.5rem 0;
      padding: 0.5rem 0.75rem;
      background: var(--pd-default-bg-light-col, #f2f9fa);
      align-self: stretch;
    }

    .action-btn {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: 1px solid transparent;
      border-radius: var(--pd-radius-sm, 4px);
      cursor: pointer;
      padding: 0.2rem;
      transition:
        background-color 0.15s ease-out,
        border-color 0.15s ease-out,
        opacity 0.15s ease-out;
      --pd-icon-size: 1rem;
      color: var(--pd-default-disabled-col, #9ca3af);
    }

    @media (hover: hover) {
      .action-btn {
        opacity: 0;
      }
      .badge:hover .action-btn,
      .badge:focus-within .action-btn,
      .badge.editing .action-btn {
        opacity: 1;
      }
    }

    .action-btn:hover {
      background: var(--pd-default-light-col, #f3f4f6);
      border-color: var(--pd-default-disabled-light-col, #e5e7eb);
      color: var(--pd-default-font-col, #374151);
    }

    .action-btn.delete-btn:hover {
      color: var(--pd-default-error-col, #ef4444);
      background: var(--pd-default-error-light-col, #fef2f2);
      border-color: var(--pd-default-error-col, #ef4444);
    }

    .badge.editing {
      padding: 0.75rem;
      align-items: flex-start;
    }

    .badge.has-color.editing {
      padding-left: 1.1rem;
    }

    .badge.editing .content {
      gap: 0.5rem;
    }

    .edit-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.4rem;
    }

    .badge.disabled .action-btn,
    .badge.readonly .action-btn {
      display: none;
    }
  `;
	}
	render() {
		if (!this.badge) return nothing;
		const hasColor = !!this.badge.color;
		const badgeClasses = {
			badge: true,
			active: this.active,
			editing: this._editing,
			dragging: this.dragging,
			ghost: this.ghost,
			disabled: this.disabled,
			readonly: this.readonly,
			"has-color": hasColor
		};
		return html`
      <div
        class=${classMap(badgeClasses)}
        part="badge"
        data-badge-id=${this.badge.id}
        style=${hasColor ? `--_badge-color: ${this.badge.color}` : ""}
      >
        ${this.active && this.numbered ? html`<span class="number" part="badge-number"
                >${this.index}</span
              >` : nothing}
        ${!this.disabled && !this.readonly && !this._editing ? html`
                <span class="drag-handle" part="drag-handle">
                  <pd-icon icon=${pdIcons.ICON_DRAG_HANDLE}></pd-icon>
                </span>
              ` : nothing}
        ${this.badge.icon ? html`<pd-icon
                class="badge-icon"
                icon=${this.badge.icon}
                part="badge-icon"
              ></pd-icon>` : nothing}

        <span class="content">
          ${this._editing ? html`
                  <pd-input
                    label=""
                    required
                    initValue="${this.badge.title}"
                    maxlength="${40}"
                    placeHolder=${msg("Titel", { id: "pdContent.badgeItem.titlePlaceholder" })}
                  ></pd-input>
                  <pd-input-area
                    label=""
                    initValue="${this.badge.description || ""}"
                    maxlength="${100}"
                    placeHolder=${msg("Beschreibung", { id: "pdContent.badgeItem.descPlaceholder" })}
                  ></pd-input-area>
                  <span class="edit-actions">
                    <pd-button
                      size="sm"
                      text=${msg("Speichern", { id: "pdContent.badgeItem.save" })}
                      @button-clicked=${this._onEditSave}
                    ></pd-button>
                    <pd-button
                      size="sm"
                      outline
                      text=${msg("Abbrechen", { id: "pdContent.badgeItem.cancel" })}
                      @button-clicked=${this._onEditCancel}
                    ></pd-button>
                  </span>
                ` : html`
                  <span class="title" part="badge-title"
                    >${this.badge.title}</span
                  >
                  ${this.badge.description ? html`<span class="description" part="badge-description"
                          >${this.badge.description}</span
                        >` : nothing}
                `}
        </span>

        ${this._renderActions()}
      </div>
    `;
	}
	_renderActions() {
		if (this.disabled || this.readonly) return nothing;
		if (this._editing) return nothing;
		const editTitle = msg("Bearbeiten", { id: "pdContent.badgeItem.editTitle" });
		const editAria = msg("Badge bearbeiten", { id: "pdContent.badgeItem.editAria" });
		const buttons = this.active ? html`
          ${this.editable ? html`
                  <button
                    class="action-btn"
                    @click=${this._onEditStart}
                    title=${editTitle}
                    aria-label=${editAria}
                  >
                    <pd-icon icon=${pdIcons.ICON_EDIT_NEW}></pd-icon>
                  </button>
                ` : nothing}
          <button
            class="action-btn"
            @click=${this._onRemove}
            title=${msg("Entfernen", { id: "pdContent.badgeItem.removeTitle" })}
            aria-label=${msg("Badge entfernen", { id: "pdContent.badgeItem.removeAria" })}
          >
            <pd-icon icon=${pdIcons.ICON_ARROW_NEXT}></pd-icon>
          </button>
        ` : html`
          ${this.editable ? html`
                  <button
                    class="action-btn"
                    @click=${this._onEditStart}
                    title=${editTitle}
                    aria-label=${editAria}
                  >
                    <pd-icon icon=${pdIcons.ICON_EDIT_NEW}></pd-icon>
                  </button>
                ` : nothing}
          <button
            class="action-btn"
            @click=${this._onAdd}
            title=${msg("Hinzufügen", { id: "pdContent.badgeItem.addTitle" })}
            aria-label=${msg("Badge hinzufügen", { id: "pdContent.badgeItem.addAria" })}
          >
            <pd-icon icon=${pdIcons.ICON_ARROW_BACK}></pd-icon>
          </button>
          ${this.badge.custom ? html`
                  <button
                    class="action-btn delete-btn"
                    @click=${this._onDelete}
                    title=${msg("Löschen", { id: "pdContent.badgeItem.deleteTitle" })}
                    aria-label=${msg("Badge löschen", { id: "pdContent.badgeItem.deleteAria" })}
                  >
                    <pd-icon icon=${pdIcons.ICON_DELETE_NEW}></pd-icon>
                  </button>
                ` : nothing}
        `;
		return html`<span class="action-zone" part="action-zone">${buttons}</span>`;
	}
	/** Programmatically enter edit mode and focus the title input. */
	async startEditing() {
		if (this._editing) return;
		this._editing = true;
		this._setupEditKeydown();
		this.dispatchEvent(new CustomEvent("badge-edit-start", {
			detail: { badgeId: this.badge.id },
			bubbles: true,
			composed: true
		}));
		await this.updateComplete;
		(this.shadowRoot?.querySelector("pd-input"))?.focus();
	}
	_dispatchEditEnd() {
		this.dispatchEvent(new CustomEvent("badge-edit-end", {
			detail: { badgeId: this.badge.id },
			bubbles: true,
			composed: true
		}));
	}
	_onEditStart(e) {
		e.stopPropagation();
		this.startEditing();
	}
	_onEditSave(e) {
		e.stopPropagation();
		const input = this.shadowRoot?.querySelector("pd-input");
		const textarea = this.shadowRoot?.querySelector("pd-input-area");
		const title = (input?.value || "").trim();
		if (!title) {
			input?.runValidators();
			return;
		}
		const description = (textarea?.value || "").trim();
		this._editing = false;
		this._teardownEditKeydown();
		this._dispatchEditEnd();
		this.dispatchEvent(new CustomEvent("badge-action", {
			detail: {
				action: "edit-save",
				badgeId: this.badge.id,
				title,
				description
			},
			bubbles: true,
			composed: true
		}));
	}
	_onEditCancel(e) {
		e.stopPropagation();
		this._editing = false;
		this._teardownEditKeydown();
		this._dispatchEditEnd();
		if (!this.badge.title) this.dispatchEvent(new CustomEvent("badge-action", {
			detail: {
				action: "delete",
				badgeId: this.badge.id
			},
			bubbles: true,
			composed: true
		}));
	}
	_onAdd(e) {
		e.stopPropagation();
		this.dispatchEvent(new CustomEvent("badge-action", {
			detail: {
				action: "add",
				badgeId: this.badge.id
			},
			bubbles: true,
			composed: true
		}));
	}
	_onRemove(e) {
		e.stopPropagation();
		this.dispatchEvent(new CustomEvent("badge-action", {
			detail: {
				action: "remove",
				badgeId: this.badge.id
			},
			bubbles: true,
			composed: true
		}));
	}
	_onDelete(e) {
		e.stopPropagation();
		this.dispatchEvent(new CustomEvent("badge-action", {
			detail: {
				action: "delete",
				badgeId: this.badge.id
			},
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ type: Object })], PdBadgeItem.prototype, "badge", void 0);
__decorate([property({ type: Number })], PdBadgeItem.prototype, "index", void 0);
__decorate([property({ type: Boolean })], PdBadgeItem.prototype, "numbered", void 0);
__decorate([property({ type: Boolean })], PdBadgeItem.prototype, "active", void 0);
__decorate([property({ type: Boolean })], PdBadgeItem.prototype, "editable", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdBadgeItem.prototype, "disabled", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdBadgeItem.prototype, "readonly", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdBadgeItem.prototype, "dragging", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdBadgeItem.prototype, "ghost", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdBadgeItem.prototype, "dimmed", void 0);
__decorate([state()], PdBadgeItem.prototype, "_editing", void 0);
//#endregion
export { PdBadgeItem };
